import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import './Login.css';
import './RoleSelection.css';
import CityAutocomplete from '../common/CityAutocomplete';
import LanguageSwitcher from '../common/LanguageSwitcher';
import backgroundImage from '../../assets/common/2nd_background.png';
import { storage } from '../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { updateProfile } from 'firebase/auth';

// Determine API base URL (keep logic consistent with AuthContext and other pages)
const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;

  // If no env URL, use localhost for development
  if (!envUrl) {
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
  }
  // If it's a placeholder URL, use localhost for development
  if (envUrl.includes('your-cloud-run-url') || envUrl.includes('placeholder')) {
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
  }
  // If env URL contains 'run.app', it's a Cloud Run URL - always use it
  if (envUrl.includes('run.app')) {
    return envUrl;
  }
  // In production builds, always use the configured URL
  if (process.env.NODE_ENV === 'production') {
    return envUrl;
  }

  // In development, use localhost
  return 'http://localhost:8081';
};

const API_BASE_URL = getApiBaseUrl();

export default function RoleSelection() {
  const [error, setError] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [businessCategory, setBusinessCategory] = useState('');
  const [businessDisplayName, setBusinessDisplayName] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');
  const [freelancerDisplayName, setFreelancerDisplayName] = useState('');
  const [freelancerPhone, setFreelancerPhone] = useState('');
  const [freelancerLocation, setFreelancerLocation] = useState('');
  const [freelancerLocationValid, setFreelancerLocationValid] = useState(false);
  const [freelancerCategory, setFreelancerCategory] = useState('');
  const [jobCategories, setJobCategories] = useState([]);
  const [businessLocation, setBusinessLocation] = useState('');
  const [businessLocationValid, setBusinessLocationValid] = useState(false);
  const [savingRole, setSavingRole] = useState(false);
  const [checkingDisplayName, setCheckingDisplayName] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  // Terms acceptance is handled on a dedicated page (/terms) after role selection.
  const fileInputRef = useRef(null);
  const { currentUser, userRoles, addUserRole, logout, loading: authLoading, switchingRole, isApproved } = useAuth();
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const formRef = useRef(null);

  // Check if business user is missing business type or display name
  const [needsBusinessType, setNeedsBusinessType] = useState(false);
  const [needsBusinessDisplayName, setNeedsBusinessDisplayName] = useState(false);
  const [checkingBusinessRequirements, setCheckingBusinessRequirements] = useState(false);
  const [businessRequirementsChecked, setBusinessRequirementsChecked] = useState(false);

  // Check if business user needs to provide business type or display name
  useEffect(() => {
    const checkBusinessRequirements = async () => {
      // Only check if we have a current user and auth is not loading
      if (!currentUser || authLoading) {
        // If no user or still loading, clear requirements and mark as not checking
        setNeedsBusinessType(false);
        setNeedsBusinessDisplayName(false);
        setCheckingBusinessRequirements(false);
        return;
      }

      // Check if user is a business user
      if (userRoles && userRoles.length > 0 && userRoles.includes('business')) {
        setCheckingBusinessRequirements(true);
        setBusinessRequirementsChecked(false);
        try {
          const { doc, getDoc } = await import('firebase/firestore');
          const { db } = await import('../../firebase');
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const missingBusinessCategory = !userData.businessCategory || !userData.businessCategory.trim();
            const missingDisplayName = !userData.displayName || !userData.displayName.trim();

            if (missingBusinessCategory || missingDisplayName) {
              setNeedsBusinessType(missingBusinessCategory);
              setNeedsBusinessDisplayName(missingDisplayName);
              setSelectedRole('business');
              if (userData.businessCategory) setBusinessCategory(userData.businessCategory);
              if (userData.displayName) setBusinessDisplayName(userData.displayName);
            } else {
              setNeedsBusinessType(false);
              setNeedsBusinessDisplayName(false);
            }
          } else {
            // User document doesn't exist, assume both are needed
            setNeedsBusinessType(true);
            setNeedsBusinessDisplayName(true);
            setSelectedRole('business');
          }
        } catch (error) {
          console.error('Error checking business requirements:', error);
          // On error, assume both are needed to be safe
          setNeedsBusinessType(true);
          setNeedsBusinessDisplayName(true);
          setSelectedRole('business');
        } finally {
          setCheckingBusinessRequirements(false);
          setBusinessRequirementsChecked(true);
        }
      } else if (userRoles && userRoles.length > 0) {
        // User has roles but not business, clear business requirements
        setNeedsBusinessType(false);
        setNeedsBusinessDisplayName(false);
        setCheckingBusinessRequirements(false);
        setBusinessRequirementsChecked(true);
      } else {
        // No roles yet or userRoles not loaded, clear business requirements
        if (userRoles !== undefined) {
          // userRoles has been loaded (even if empty), so we're done checking
          setNeedsBusinessType(false);
          setNeedsBusinessDisplayName(false);
          setCheckingBusinessRequirements(false);
          setBusinessRequirementsChecked(true);
        }
      }
    };
    checkBusinessRequirements();
  }, [currentUser, userRoles, authLoading]);

  // Redirect if user already has roles and doesn't need to fill business requirements
  useEffect(() => {
    if (authLoading || checkingBusinessRequirements) {
      return;
    }

    const isAdmin = userRoles && userRoles.includes('admin');
    // Check if user is Google user (auto-approved) or if explicitly approved
    const isGoogleUser = currentUser?.providerData?.some(
      (provider) => provider.providerId === 'google.com'
    );
    const isApprovedUser = isAdmin || !!isApproved || isGoogleUser;

    if (currentUser && isApprovedUser) {
      // If user has roles and doesn't need business requirements, redirect to dashboard
      if (userRoles && userRoles.length > 0) {
        if (userRoles.includes('business')) {
          if (businessRequirementsChecked && !needsBusinessType && !needsBusinessDisplayName) {
            navigate('/business/dashboard', { replace: true });
            return;
          }
        } else {
          // User has roles and is not business, or business requirements are met
          const defaultPath = userRoles.includes('freelancer')
            ? '/freelancer/dashboard'
            : userRoles.includes('business')
              ? '/business/dashboard'
              : '/profile';
          navigate(defaultPath, { replace: true });
          return;
        }
      }
    } else if (currentUser && !isApprovedUser) {
      // User is not approved, redirect to login
      navigate('/login', { replace: true });
      return;
    } else if (!currentUser && !authLoading) {
      // User is not logged in, redirect to login
      navigate('/login', { replace: true });
      return;
    }
  }, [currentUser, userRoles, authLoading, checkingBusinessRequirements, needsBusinessType, needsBusinessDisplayName, businessRequirementsChecked, isApproved, navigate]);

  // Clear form fields when switching roles
  useEffect(() => {
    if (switchingRole || (userRoles && userRoles.length === 0)) {
      setBusinessType('');
      setBusinessCategory('');
      setBusinessDisplayName('');
      setBusinessPhone('');
      setFreelancerDisplayName('');
      setFreelancerCategory('');
      setBusinessLocation('');
      setBusinessLocationValid(false);
      setSelectedRole('');
    }
  }, [switchingRole, userRoles]);

  // Load job categories when freelancer or business role is selected
  useEffect(() => {
    const fetchJobCategories = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/job-categories`);
        if (!res.ok) return;
        const data = await res.json();
        const categories = Array.isArray(data.categories) ? data.categories : [];
        setJobCategories(categories);
      } catch (e) {
        console.warn('Failed to load job categories', e);
      }
    };

    if ((selectedRole === 'freelancer' || selectedRole === 'business') && jobCategories.length === 0) {
      fetchJobCategories();
    }
  }, [selectedRole, jobCategories.length]);

  // Israeli phone validation
  const isValidIsraeliPhone = (input) => {
    if (!input) return false;
    const normalized = input.replace(/[\s\-]/g, '');
    let local = normalized;
    if (local.startsWith('+972')) {
      local = '0' + local.slice(4);
    }
    const landline = /^0[23489]\d{7}$/;     // e.g., 03-XXXXXXX
    const mobileOrVoip = /^0[57]\d{8}$/;    // e.g., 05X-XXXXXXX, 07X-XXXXXXX
    return landline.test(local) || mobileOrVoip.test(local);
  };

  // Handle profile picture upload
  const handleProfilePictureChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert(t('profile.invalidImageType') || 'Please select an image file.');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      alert(t('profile.imageTooLarge') || 'Image size must be less than 5MB.');
      return;
    }

    setProfilePicture(file);

    // Clear profile picture error when user uploads
    if (validationErrors.profilePicture) {
      setValidationErrors({ ...validationErrors, profilePicture: null });
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfilePicturePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Upload profile picture to Firebase Storage
  const uploadProfilePicture = async () => {
    if (!profilePicture || !currentUser) return null;

    setUploadingPhoto(true);
    try {
      // Create a reference to the file location
      const fileExtension = profilePicture.name.split('.').pop();
      const photoRef = ref(storage, `profile-photos/${currentUser.uid}.${fileExtension}`);

      // Upload the file
      const metadata = {
        contentType: profilePicture.type,
      };
      await uploadBytes(photoRef, profilePicture, metadata);

      // Get download URL
      const downloadURL = await getDownloadURL(photoRef);

      // Update Firebase Auth profile
      await updateProfile(currentUser, {
        photoURL: downloadURL
      });

      setUploadingPhoto(false);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      setUploadingPhoto(false);
      alert(t('profile.uploadError') || 'Failed to upload profile picture. Please try again.');
      return null;
    }
  };

  // Handle camera icon click
  const handleCameraClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
    // Clear profile picture error when user clicks to upload
    if (validationErrors.profilePicture) {
      setValidationErrors({ ...validationErrors, profilePicture: null });
    }
  };

  const handleRoleConfirm = async () => {
    const isFirstRoleSelection = !!currentUser && ((userRoles || []).length === 0);

    // Clear previous validation errors
    setValidationErrors({});
    setShowValidationErrors(false);

    // If needs business type or display name (existing business user), just update them
    if ((needsBusinessType || needsBusinessDisplayName) && currentUser) {
      const errors = {};
      let hasErrors = false;

      if (jobCategories.length > 0 && !businessCategory) {
        errors.businessCategory = t('login.businessCategoryRequired') || 'Please select a category';
        hasErrors = true;
      }
      if (!businessDisplayName.trim()) {
        errors.businessDisplayName = t('login.businessDisplayNameRequired') || 'Business display name is required';
        hasErrors = true;
      }
      if (!businessLocation.trim() || !businessLocationValid) {
        errors.businessLocation = t('login.citySelectionRequired') || 'Please select a city from the list';
        hasErrors = true;
      }
        if (!profilePicture) {
          errors.profilePicture = t('login.profilePictureRequired') || 'Please upload a profile picture';
          hasErrors = true;
        }

      if (hasErrors) {
        setValidationErrors(errors);
        setShowValidationErrors(true);
        // Scroll to first error
        setTimeout(() => {
          const firstErrorField = document.querySelector('.field-error');
          if (firstErrorField) {
            firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
        return;
      }

      setSavingRole(true);
      setCheckingDisplayName(true);
      try {
        // Upload profile picture first
        const photoURL = await uploadProfilePicture();
        if (!photoURL) {
          setSavingRole(false);
          setCheckingDisplayName(false);
          return;
        }

        // Check for duplicate display name via API
        const token = await currentUser.getIdToken();

        // Check if display name is already taken
        const checkResponse = await fetch(`${API_BASE_URL}/api/user/check-display-name?displayName=${encodeURIComponent(businessDisplayName.trim())}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        setCheckingDisplayName(false);

        if (checkResponse.ok) {
          const checkData = await checkResponse.json();
          if (!checkData.available) {
            alert(t('login.displayNameTaken'));
            setSavingRole(false);
            return;
          }
        }

        // Use backend API to update profile (ensures validation)
        const updateData = {
          businessCategory: businessCategory || null,
          displayName: businessDisplayName.trim(),
          location: businessLocation.trim(),
          photoURL: photoURL
        };

        let updateResponse;
        try {
          const apiUrl = `${API_BASE_URL}/api/user/profile`;
          console.log('Attempting to update profile at:', apiUrl);
          updateResponse = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(updateData)
          });
        } catch (networkError) {
          throw new Error(`Network error: Unable to connect to server at ${API_BASE_URL}. Please check your connection and ensure the backend is running.`);
        }

        if (!updateResponse.ok) {
          let errorMessage = 'Failed to update profile';
          try {
            const errorData = await updateResponse.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
          } catch (parseError) {
            errorMessage = updateResponse.statusText || errorMessage;
          }
          throw new Error(errorMessage);
        }
        setNeedsBusinessType(false);
        setNeedsBusinessDisplayName(false);
        setSavingRole(false);
        // After updating profile, wait a moment for Firestore to propagate the update
        setTimeout(() => {
          navigate('/business/dashboard', { replace: true });
        }, 1000);
      } catch (error) {
        // Error saving business requirements
        if (error.message && error.message.includes('Display name already taken')) {
          alert(t('login.displayNameTaken'));
        } else if (error.message && error.message.includes('Display name is required')) {
          alert(t('login.businessDisplayNameRequired'));
        } else if (error.message && error.message.includes('Unauthorized')) {
          alert(t('login.failedToSaveRole') + ' (Unauthorized)');
        } else if (error.message && error.message.includes('Network error')) {
          alert(error.message);
        } else if (error.message && error.message.includes('Failed to update profile')) {
          alert(error.message);
        } else {
          alert(t('login.failedToSaveRole') + (error.message ? ': ' + error.message : ''));
        }
        setSavingRole(false);
        setCheckingDisplayName(false);
      }
      return;
    }

    if (selectedRole && currentUser) {
      // Get display name based on role
      const displayName = selectedRole === 'business' ? businessDisplayName.trim() : freelancerDisplayName.trim();

      // Validate based on role and show visual errors
      const errors = {};
      let hasErrors = false;

      if (selectedRole === 'business') {
        if (jobCategories.length > 0 && !businessCategory) {
          errors.businessCategory = t('login.businessCategoryRequired') || 'Please select a category';
          hasErrors = true;
        }
        if (!displayName) {
          errors.businessDisplayName = t('login.businessDisplayNameRequired') || 'Business display name is required';
          hasErrors = true;
        }
        if (!businessLocation.trim() || !businessLocationValid) {
          errors.businessLocation = t('login.citySelectionRequired') || 'Please select a city from the list';
          hasErrors = true;
        }
        if (!profilePicture) {
          errors.profilePicture = t('login.profilePictureRequired') || 'Please upload a profile picture';
          hasErrors = true;
        }
      } else if (selectedRole === 'freelancer') {
        if (!displayName) {
          errors.freelancerDisplayName = t('login.displayNameRequired') || 'Display name is required';
          hasErrors = true;
        }
        if (!freelancerLocation.trim() || !freelancerLocationValid) {
          errors.freelancerLocation = t('login.citySelectionRequired') || 'Please select a city from the list';
          hasErrors = true;
        }
        if (jobCategories.length > 0 && !freelancerCategory) {
          errors.freelancerCategory = t('login.freelancerCategoryRequired') || 'Please select a category';
          hasErrors = true;
        }
        if (!profilePicture) {
          errors.profilePicture = t('login.profilePictureRequired') || 'Please upload a profile picture';
          hasErrors = true;
        }
      }

      if (hasErrors) {
        setValidationErrors(errors);
        setShowValidationErrors(true);
        // Scroll to first error
        setTimeout(() => {
          const firstErrorField = document.querySelector('.field-error, .photo-error');
          if (firstErrorField) {
            firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
        return;
      }

      setSavingRole(true);
      setCheckingDisplayName(true);
      try {
        // Check for duplicate display name before adding role (for both business and freelancer)
        const token = await currentUser.getIdToken();

        const checkResponse = await fetch(`${API_BASE_URL}/api/user/check-display-name?displayName=${encodeURIComponent(displayName)}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        setCheckingDisplayName(false);

        if (checkResponse.ok) {
          const checkData = await checkResponse.json();
          if (!checkData.available) {
            alert(t('login.displayNameTaken'));
            setSavingRole(false);
            return;
          }
        }

        // Upload profile picture first
        const photoURL = await uploadProfilePicture();
        if (!photoURL) {
          setSavingRole(false);
          setCheckingDisplayName(false);
          return;
        }

        console.log('Attempting to add role:', selectedRole);
        await addUserRole(
          currentUser.uid,
          selectedRole,
          selectedRole === 'business' ? businessCategory : null,
          displayName
        );
        console.log('Role added, navigating to dashboard');
        // Save contact info if applicable
        try {
          if (selectedRole === 'freelancer') {
            const token2 = await currentUser.getIdToken();
            await fetch(`${API_BASE_URL}/api/user/profile`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token2}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                displayName,
                location: freelancerLocation.trim(),
                freelancerCategory: freelancerCategory || null,
                photoURL: photoURL
              })
            });
          } else if (selectedRole === 'business') {
            const token2 = await currentUser.getIdToken();
            await fetch(`${API_BASE_URL}/api/user/profile`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token2}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                displayName,
                businessCategory: businessCategory || null,
                location: businessLocation.trim(),
                photoURL: photoURL
              })
            });
          }
        } catch (err) {
          console.warn('Failed to save contact info', err);
        }
        // For brand-new signups: collect Terms acceptance BEFORE KYC.
        if (isFirstRoleSelection) {
          navigate('/terms', { replace: true, state: { next: '/kyc' } });
          return;
        }

        const targetPath = selectedRole === 'freelancer' ? '/freelancer/dashboard' : '/business/dashboard';
        navigate(targetPath);
      } catch (error) {
        if (error.message && error.message.includes('Display name already taken')) {
          alert(t('login.displayNameTaken'));
        } else {
          alert(t('login.failedToSaveRole'));
        }
        setSavingRole(false);
        setCheckingDisplayName(false);
      }
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      setError(t('login.failedToLogout') || 'Failed to logout');
    }
  };

  // Show loading state while checking auth and roles or business requirements
  if (authLoading || checkingBusinessRequirements || switchingRole) {
    return (
      <div className={`loading-screen ${isRTL ? 'rtl' : ''}`}>
        <div className="loading-spinner-container">
          <span className="spinner" style={{ width: '32px', height: '32px', borderWidth: '3px', borderColor: '#FFC720', borderTopColor: '#FFD966' }}></span>
          <p className="loading-text">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Show business requirements form if needed
  if (needsBusinessType || needsBusinessDisplayName) {
    return (
      <div className={`login-container ${isRTL ? 'rtl' : ''}`}>
        <div className="login-card">
          <div className="login-header">
            <h1>{t('login.businessRequirements')}</h1>
            <p>{t('login.businessRequirementsMessage')}</p>
          </div>

          {error && <div className="error-message">{error}</div>}

          {jobCategories.length > 0 && (
            <div className="business-type-input-container">
              <label htmlFor="business-category-req" className="business-type-label">
                {t('login.businessCategory') || t('login.freelancerCategory') || 'Category'} <span className="required">*</span>
              </label>
              <select
                id="business-category-req"
                className="business-type-input"
                value={businessCategory}
                onChange={(e) => {
                  setBusinessCategory(e.target.value);
                  if (validationErrors.businessCategory) {
                    setValidationErrors({ ...validationErrors, businessCategory: null });
                  }
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && businessCategory && businessDisplayName.trim()) {
                    handleRoleConfirm();
                  }
                }}
                autoFocus={needsBusinessType}
                style={showValidationErrors && validationErrors.businessCategory ? { borderColor: '#EF4444', borderWidth: '2px' } : {}}
              >
                <option value="">{t('login.businessCategoryPlaceholder') || t('login.freelancerCategoryPlaceholder') || 'בחר קטגוריה'}</option>
                {jobCategories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
              {showValidationErrors && validationErrors.businessCategory && (
                <div style={{ color: '#EF4444', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                  {validationErrors.businessCategory}
                </div>
              )}
            </div>
          )}
          <div className="business-type-input-container">
            <label htmlFor="business-display-name" className="business-type-label">
              {t('login.businessDisplayName')} <span className="required">*</span>
            </label>
            <input
              id="business-display-name"
              type="text"
              className="business-type-input"
              placeholder={t('login.businessDisplayNamePlaceholder')}
              value={businessDisplayName}
              onChange={(e) => setBusinessDisplayName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && businessType.trim() && businessDisplayName.trim()) {
                  handleRoleConfirm();
                }
              }}
              autoFocus={needsBusinessDisplayName && !needsBusinessType}
            />
            <p className="business-type-hint">{t('login.businessDisplayNameHint')}</p>
          </div>
          <div className="business-type-input-container">
            <label htmlFor="business-location-req" className="business-type-label">
              {t('jobs.location')} <span className="required">*</span>
            </label>
            <CityAutocomplete
              inputId="business-location-req"
              value={businessLocation}
              onChange={setBusinessLocation}
              onValidityChange={setBusinessLocationValid}
              onSelectionComplete={() => {
                if (jobCategories.length > 0 && !businessCategory) {
                  alert(t('login.businessCategoryRequired') || t('login.freelancerCategoryRequired') || 'Please select a category');
                  return;
                }
                if (!businessDisplayName.trim()) {
                  alert(t('login.businessDisplayNameRequired'));
                  return;
                }
              }}
              enforceSelection
              placeholder={t('login.cityPlaceholder') || 'Type your city (Hebrew or English)'}
              required
            />
          </div>

          <button
            className={`role-confirm-btn ${(businessCategory && businessDisplayName.trim() && businessLocationValid && profilePicture) ? 'active' : ''}`}
            onClick={handleRoleConfirm}
            disabled={
              savingRole ||
              uploadingPhoto
            }
          >
            {savingRole || uploadingPhoto ? (
              <span className="button-loading">
                <span className="spinner spinner-small"></span>
                {uploadingPhoto
                  ? (t('profile.uploadingPhoto') || 'Uploading photo...')
                  : checkingDisplayName
                    ? (t('login.checkingDisplayName') || 'Checking availability...')
                    : t('common.saving')}
              </span>
            ) : (
              t('common.continue')
            )}
          </button>
          <br />
          <br />
          <button
            onClick={handleLogout}
            className="logout-btn"
          >
            {t('dashboard.logout')}
          </button>
        </div>
      </div>
    );
  }

  // Show role selection screen matching the design
  return (
    <div className={`role-selection-page ${isRTL ? 'rtl' : ''}`}>
      {/* Header Section */}
      <div className="role-selection-header">
        <h1 className="role-selection-brand">{t('app.name')}</h1>
        <p className="role-selection-tagline">{t('login.tagline')}</p>
      </div>

      {/* Main Toggle Section */}
      <div className="role-selection-main">
        <div className="role-toggle-container">
          <div className="role-toggle-wrapper">
            <div
              className="role-toggle-slider"
              style={{
                transform: selectedRole === 'freelancer'
                  ? 'translateX(0%)'
                  : selectedRole === 'business'
                    ? 'translateX(100%)'
                    : 'translateX(0%)',
                opacity: selectedRole ? 1 : 0
              }}
            ></div>
            <button
              className={`role-toggle-option ${selectedRole === 'freelancer' ? 'active' : ''}`}
              onClick={() => {
                setSelectedRole('freelancer');
                if (!freelancerDisplayName.trim()) {
                  setTimeout(() => {
                    if (formRef.current) {
                      const yOffset = -20;
                      const y = formRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
                      window.scrollTo({ top: y, behavior: 'smooth' });
                    }
                  }, 150);
                } else {
                  handleRoleConfirm();
                }
              }}
            >
              <div className="role-toggle-icon">
                <img src="/wrench_icon.png" alt="Wrench icon" className="role-toggle-icon-img" />
              </div>
              <div className="role-toggle-text">
                <span className="role-toggle-title">{t('login.lookingForWork')}</span>
                <span className="role-toggle-description">{t('login.lookingForWorkDescription')}</span>
              </div>
            </button>
            <button
              className={`role-toggle-option ${selectedRole === 'business' ? 'active' : ''}`}
              onClick={() => {
                setSelectedRole('business');
                if (!businessCategory || !businessDisplayName.trim()) {
                  setTimeout(() => {
                    if (formRef.current) {
                      const yOffset = -20;
                      const y = formRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
                      window.scrollTo({ top: y, behavior: 'smooth' });
                    }
                  }, 150);
                } else {
                  handleRoleConfirm();
                }
              }}
            >
              <div className="role-toggle-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 7V3H2V21H22V7H12ZM6 19H4V17H6V19ZM6 15H4V13H6V15ZM6 11H4V9H6V11ZM6 7H4V5H6V7ZM10 19H8V17H10V19ZM10 15H8V13H10V15ZM10 11H8V9H10V11ZM10 7H8V5H10V7ZM20 19H12V9H20V19ZM18 11H14V13H18V11ZM18 15H14V17H18V15Z" fill="currentColor"/>
                </svg>
              </div>
              <div className="role-toggle-text">
                <span className="role-toggle-title">{t('login.hiringWorkers')}</span>
                <span className="role-toggle-description">{t('login.hiringWorkersDescription')}</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Display Name Input for Freelancer */}
      {selectedRole === 'freelancer' && (
        <>
          <div className="role-selection-background"></div>
          <div className="role-selection-profile-container">
            <div className="role-selection-profile-outer">
              <div className="role-selection-profile-inner">
                {profilePicturePreview ? (
                  <img
                    src={profilePicturePreview}
                    alt="Profile preview"
                    className="role-selection-profile-image"
                  />
                ) : null}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleProfilePictureChange}
                style={{ display: 'none' }}
              />
              <div
                className="role-selection-camera-icon"
                onClick={handleCameraClick}
                style={{ cursor: 'pointer', flexDirection: 'column' }}
              >
                <img
                  src="/camera_vector.svg"
                  alt={t('login.uploadProfilePicture') || 'Upload profile picture'}
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </div>
            </div>
            {showValidationErrors && validationErrors.profilePicture && (
              <div style={{ color: '#EF4444', fontSize: '0.875rem', marginTop: '-3rem', textAlign: 'center' }}>
                {validationErrors.profilePicture}
              </div>
            )}
          </div>
          <div className="role-selection-form-section" ref={formRef}>
            <h2 className="role-selection-form-title">{t('login.personalDetails')}</h2>
            <div className="role-selection-form-field">
              <input
                id="freelancer-display-name"
                type="text"
                placeholder={t('login.displayNamePlaceholder') || 'שם מלא'}
                value={freelancerDisplayName}
                onChange={(e) => {
                  setFreelancerDisplayName(e.target.value);
                  // Clear error when user starts typing
                  if (validationErrors.freelancerDisplayName) {
                    setValidationErrors({ ...validationErrors, freelancerDisplayName: null });
                  }
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && freelancerDisplayName.trim()) {
                    handleRoleConfirm();
                  }
                }}
                style={showValidationErrors && validationErrors.freelancerDisplayName ? { borderColor: '#EF4444', borderWidth: '2px' } : {}}
              />
              {showValidationErrors && validationErrors.freelancerDisplayName && (
                <div style={{ color: '#EF4444', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                  {validationErrors.freelancerDisplayName}
                </div>
              )}
            </div>
            <div className="role-selection-form-field">
              <div style={showValidationErrors && validationErrors.freelancerLocation ? { border: '2px solid #EF4444', borderRadius: '8px', padding: '2px' } : {}}>
                <CityAutocomplete
                  inputId="freelancer-location"
                  value={freelancerLocation}
                  onChange={(value) => {
                    setFreelancerLocation(value);
                    // Clear error when user starts selecting
                    if (validationErrors.freelancerLocation) {
                      setValidationErrors({ ...validationErrors, freelancerLocation: null });
                    }
                  }}
                  onValidityChange={(valid) => {
                    setFreelancerLocationValid(valid);
                    if (valid && validationErrors.freelancerLocation) {
                      setValidationErrors({ ...validationErrors, freelancerLocation: null });
                    }
                  }}
                  onSelectionComplete={() => {
                    if (!freelancerDisplayName.trim()) {
                      alert(t('login.displayNameRequired'));
                      return;
                    }
                  }}
                  enforceSelection
                  placeholder={t('login.locationPlaceholder') || 'מיקום'}
                  required
                />
              </div>
              {showValidationErrors && validationErrors.freelancerLocation && (
                <div style={{ color: '#EF4444', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                  {validationErrors.freelancerLocation}
                </div>
              )}
            </div>
            {jobCategories.length > 0 && (
              <div className="role-selection-form-field">
                <select
                  id="freelancer-category"
                  value={freelancerCategory}
                  onChange={(e) => {
                    setFreelancerCategory(e.target.value);
                    // Clear error when user selects
                    if (validationErrors.freelancerCategory) {
                      setValidationErrors({ ...validationErrors, freelancerCategory: null });
                    }
                  }}
                  style={showValidationErrors && validationErrors.freelancerCategory ? { borderColor: '#EF4444', borderWidth: '2px' } : {}}
                >
                  <option value="">{t('login.freelancerCategoryPlaceholder') || 'בחר קטגוריה'}</option>
                  {jobCategories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
                {showValidationErrors && validationErrors.freelancerCategory && (
                  <div style={{ color: '#EF4444', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                    {validationErrors.freelancerCategory}
                  </div>
                )}
              </div>
            )}
          </div>
          <button
            className="role-selection-continue-button"
            onClick={handleRoleConfirm}
            disabled={
              savingRole ||
              uploadingPhoto
            }
            style={{
              position: 'relative'
            }}
          >
            {savingRole || uploadingPhoto ? (
              <span className="button-loading">
                <span className="spinner spinner-small"></span>
                {uploadingPhoto
                  ? (t('profile.uploadingPhoto') || 'Uploading photo...')
                  : checkingDisplayName
                    ? (t('login.checkingDisplayName') || 'Checking availability...')
                    : t('common.saving')}
              </span>
            ) : (
              <span className="role-selection-continue-button-text">{t('common.continue') || 'continue'}</span>
            )}
          </button>
          {showValidationErrors && validationErrors.profilePicture && (
            <div style={{ color: '#EF4444', fontSize: '0.875rem', marginTop: '0.5rem', textAlign: 'center' }}>
              {validationErrors.profilePicture}
            </div>
          )}
        </>
      )}

      {/* Business Type Input (shown when business is selected) */}
      {selectedRole === 'business' && (
        <>
          <div className="role-selection-background"></div>
          <div className="role-selection-profile-container">
            <div className="role-selection-profile-outer">
              <div className="role-selection-profile-inner">
                {profilePicturePreview ? (
                  <img
                    src={profilePicturePreview}
                    alt="Profile preview"
                    className="role-selection-profile-image"
                  />
                ) : null}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleProfilePictureChange}
                style={{ display: 'none' }}
              />
              <div
                className="role-selection-camera-icon"
                onClick={handleCameraClick}
                style={{ cursor: 'pointer' }}
              >
                <img
                  src="/camera_vector.svg"
                  alt={t('login.uploadProfilePicture') || 'Upload profile picture'}
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </div>
            </div>
            {showValidationErrors && validationErrors.profilePicture && (
              <div style={{ color: '#EF4444', fontSize: '0.875rem', marginTop: '0.5rem', textAlign: 'center' }}>
                {validationErrors.profilePicture}
              </div>
            )}
          </div>
          <div className="role-selection-form-section" ref={formRef}>
            <h2 className="role-selection-form-title">{t('login.personalDetails')}</h2>
            <div className="role-selection-form-field">
              <input
                id="business-display-name"
                type="text"
                placeholder={t('login.businessDisplayNamePlaceholder') || 'שם העסק'}
                value={businessDisplayName}
                onChange={(e) => {
                  setBusinessDisplayName(e.target.value);
                  // Clear error when user starts typing
                  if (validationErrors.businessDisplayName) {
                    setValidationErrors({ ...validationErrors, businessDisplayName: null });
                  }
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && businessCategory && businessDisplayName.trim()) {
                    handleRoleConfirm();
                  }
                }}
                style={showValidationErrors && validationErrors.businessDisplayName ? { borderColor: '#EF4444', borderWidth: '2px' } : {}}
              />
              {showValidationErrors && validationErrors.businessDisplayName && (
                <div style={{ color: '#EF4444', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                  {validationErrors.businessDisplayName}
                </div>
              )}
            </div>
            {jobCategories.length > 0 && (
              <div className="role-selection-form-field">
                <select
                  id="business-category"
                  value={businessCategory}
                  onChange={(e) => {
                    setBusinessCategory(e.target.value);
                    // Clear error when user selects
                    if (validationErrors.businessCategory) {
                      setValidationErrors({ ...validationErrors, businessCategory: null });
                    }
                  }}
                  style={showValidationErrors && validationErrors.businessCategory ? { borderColor: '#EF4444', borderWidth: '2px' } : {}}
                >
                  <option value="">{t('login.businessCategoryPlaceholder') || t('login.freelancerCategoryPlaceholder') || 'בחר קטגוריה'}</option>
                  {jobCategories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
                {showValidationErrors && validationErrors.businessCategory && (
                  <div style={{ color: '#EF4444', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                    {validationErrors.businessCategory}
                  </div>
                )}
              </div>
            )}
            <div className="role-selection-form-field">
              <div style={showValidationErrors && validationErrors.businessLocation ? { border: '2px solid #EF4444', borderRadius: '8px', padding: '2px' } : {}}>
                <CityAutocomplete
                  inputId="business-location"
                  value={businessLocation}
                  onChange={(value) => {
                    setBusinessLocation(value);
                    // Clear error when user starts selecting
                    if (validationErrors.businessLocation) {
                      setValidationErrors({ ...validationErrors, businessLocation: null });
                    }
                  }}
                  onValidityChange={(valid) => {
                    setBusinessLocationValid(valid);
                    if (valid && validationErrors.businessLocation) {
                      setValidationErrors({ ...validationErrors, businessLocation: null });
                    }
                  }}
                  onSelectionComplete={() => {
                    if (!businessDisplayName.trim()) {
                      alert(t('login.businessDisplayNameRequired'));
                      return;
                    }
                    if (jobCategories.length > 0 && !businessCategory) {
                      alert(t('login.businessCategoryRequired') || t('login.freelancerCategoryRequired') || 'Please select a category');
                      return;
                    }
                  }}
                  enforceSelection
                  placeholder={t('login.locationPlaceholder') || 'מיקום'}
                  required
                />
              </div>
              {showValidationErrors && validationErrors.businessLocation && (
                <div style={{ color: '#EF4444', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                  {validationErrors.businessLocation}
                </div>
              )}
            </div>
          </div>
          <button
            className="role-selection-continue-button"
            onClick={handleRoleConfirm}
            disabled={
              savingRole ||
              uploadingPhoto
            }
            style={{
              position: 'relative'
            }}
          >
            {savingRole || uploadingPhoto ? (
              <span className="button-loading">
                <span className="spinner spinner-small"></span>
                {uploadingPhoto
                  ? (t('profile.uploadingPhoto') || 'Uploading photo...')
                  : checkingDisplayName
                    ? (t('login.checkingDisplayName') || 'Checking availability...')
                    : t('common.saving')}
              </span>
            ) : (
              <span className="role-selection-continue-button-text">{t('common.continue') || 'continue'}</span>
            )}
          </button>
          {showValidationErrors && validationErrors.profilePicture && (
            <div style={{ color: '#EF4444', fontSize: '0.875rem', marginTop: '0.5rem', textAlign: 'center' }}>
              {validationErrors.profilePicture}
            </div>
          )}
        </>
      )}

      {/* Logout Button */}
      <div className="role-selection-logout">
        <button
          onClick={handleLogout}
          className="logout-btn"
        >
          {t('dashboard.logout')}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
    </div>
  );
}
